import {
  CartLinesUpdateEvent,
  CartErrorEvent,
  StandardEvents
} from '@shopify/events';

if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.addEventListener(
          'click',
          this.handleClick
        );

        this.addEventListener(
          'change',
          this.handleChange
        );

        document.addEventListener(
          StandardEvents.cartLinesUpdate,
          this.handleCartUpdate
        );

        this.loadRecommendations();
      }

      disconnectedCallback() {
        this.removeEventListener(
          'click',
          this.handleClick
        );

        this.removeEventListener(
          'change',
          this.handleChange
        );

        document.removeEventListener(
          StandardEvents.cartLinesUpdate,
          this.handleCartUpdate
        );
      }

      /*
       * Horizon preserves this custom element during
       * cart section hydration instead of reconnecting it.
       *
       * Listen for the completed cart update and explicitly
       * reload recommendations.
       */
      handleCartUpdate = (event) => {
        if (!event.promise) return;

        event.promise
          .then(({ detail }) => {
            /*
             * Update our cart product IDs immediately from
             * the returned cart data.
             */
            if (Array.isArray(detail?.items)) {
              const productIds = [
                ...new Set(
                  detail.items
                    .map((item) => item.product_id)
                    .filter(Boolean)
                    .map(String)
                ),
              ];

              this.dataset.cartProductIds =
                productIds.join(',');
            }

            /*
             * Horizon will also morph cart-drawer-section
             * after this promise resolves.
             *
             * Wait until the next frame so that morph finishes
             * before we reinsert our recommendations.
             */
            requestAnimationFrame(() => {
              if (!this.isConnected) return;

              this.loadRecommendations();
            });
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') {
              console.warn(
                'Recommendation cart update failed:',
                error
              );
            }
          });
      };

      handleClick = (event) => {
        const button = event.target.closest(
          '[data-recommendation-add]'
        );

        if (!button || !this.contains(button)) return;

        this.addProduct(button);
      };

      handleChange = (event) => {
        const select = event.target.closest(
          '[data-recommendation-variant]'
        );

        if (!select || !this.contains(select)) return;

        const row = select.closest(
          '[data-recommendation-product-id]'
        );

        if (!row) return;

        const selectedOption =
          select.options[select.selectedIndex];

        if (!selectedOption) return;

        const button = row.querySelector(
          '[data-recommendation-add]'
        );

        const price = row.querySelector(
          '[data-recommendation-price]'
        );

        const available =
          selectedOption.dataset.available === 'true';

        /*
         * Change the variant that Add will submit.
         */
        if (button) {
          button.dataset.variantId =
            selectedOption.value;

          button.disabled = !available;
        }

        /*
         * Update displayed variant price.
         */
        if (
          price &&
          selectedOption.dataset.price
        ) {
          price.textContent =
            selectedOption.dataset.price;
        }
      };

      async loadRecommendations() {
        const productId = this.dataset.productId;

        const manualTemplate =
          document.querySelector(
            '#cart-recommendations-manual-template'
          );

        /*
         * Recommendations disabled in Theme Editor.
         */
        const enabled =
          manualTemplate?.dataset.enabled !== 'false';

        if (!enabled) {
          this.replaceChildren();
          return;
        }

        if (!productId) {
          this.replaceChildren();
          return;
        }

        const manualCount = Number(
          manualTemplate?.dataset.manualCount || 0
        );
  
        if (
          manualTemplate &&
          manualCount > 0
        ) {
          const fragment =
            manualTemplate.content.cloneNode(true);

          this.replaceChildren(fragment);

          this.filterProductsAlreadyInCart();

          return;
        }

        /*
         * No manually selected products:
         * use Shopify related recommendations.
         */
        const url =
          `${window.Shopify.routes.root}recommendations/products` +
          `?product_id=${productId}` +
          `&limit=4` +
          `&section_id=cart-drawer-recommendations` +
          `&intent=related`;

        try {
          const response = await fetch(url);

          if (!response.ok) {
            console.error(
              `Recommendations request failed: ${response.status}`,
              url
            );

            return;
          }

          const text = await response.text();

          const html =
            new DOMParser().parseFromString(
              text,
              'text/html'
            );

          const recommendations =
            html.querySelector(
              '.cart-recommendations__inner'
            );

          if (
            !recommendations ||
            Number(
              recommendations.dataset
                .recommendationsCount || 0
            ) === 0
          ) {
            this.replaceChildren();
            return;
          }

          this.replaceChildren(
            recommendations.cloneNode(true)
          );

          this.filterProductsAlreadyInCart();
        } catch (error) {
          console.error(
            'Unable to load cart recommendations:',
            error
          );
        }
      }

      filterProductsAlreadyInCart() {
        const cartProductIds = new Set(
          (
            this.dataset.cartProductIds || ''
          )
            .split(',')
            .filter(Boolean)
        );

        const products =
          this.querySelectorAll(
            '[data-recommendation-product-id]'
          );

        for (const product of products) {
          const productId =
            product.dataset.recommendationProductId;

          if (
            productId &&
            cartProductIds.has(productId)
          ) {
            product.remove();
          }
        }

        /*
         * If every recommendation is already in cart,
         * clear the entire area including heading.
         */
        if (
          !this.querySelector(
            '[data-recommendation-product-id]'
          )
        ) {
          this.replaceChildren();
        }
      }

      async addProduct(button) {
        const variantId =
          button.dataset.variantId;

        const productId =
          button.dataset.productId;

        if (
          !variantId ||
          button.disabled
        ) {
          return;
        }

        const originalText =
          button.textContent;

        button.disabled = true;
        button.setAttribute(
          'aria-busy',
          'true'
        );

        button.textContent = 'Adding…';

        /*
         * Get all sections Horizon needs refreshed.
         */
        const cartItemsComponents =
          Array.from(
            document.querySelectorAll(
              'cart-items-component'
            )
          );

        const sectionIds = [
          ...new Set(
            cartItemsComponents
              .map(
                (component) =>
                  component.dataset.sectionId
              )
              .filter(Boolean)
          ),
        ];

        /*
         * Create Horizon's native cart update promise.
         */
        const deferredEventPromise =
          CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: [
              {
                merchandiseId:
                  variantId,
                quantity: 1,
              },
            ],
            promise:
              deferredEventPromise.promise,
          })
        );

        try {
          const response = await fetch(
            Theme.routes.cart_add_url,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',
                Accept:
                  'application/json',
              },

              body: JSON.stringify({
                items: [
                  {
                    id: Number(
                      variantId
                    ),
                    quantity: 1,
                  },
                ],

                sections:
                  sectionIds.join(','),
              }),
            }
          );

          const data =
            await response.json();

          if (
            !response.ok ||
            data.status
          ) {
            const message =
              data.description ||
              data.message ||
              'Unable to add product to cart';

            this.dispatchEvent(
              new CartErrorEvent({
                error: message,
                code: 'INVALID',
                detail: {
                  description:
                    data.description,
                  errors:
                    data.errors,
                },
              })
            );

            throw new Error(message);
          }

          /*
           * Fetch updated cart data.
           */
          let cart;

          const cartItemsComponent =
            cartItemsComponents[0];

          if (
            cartItemsComponent &&
            typeof cartItemsComponent
              .fetchCartData ===
              'function'
          ) {
            cart =
              await cartItemsComponent.fetchCartData();
          } else {
            const cartResponse =
              await fetch(
                `${Theme.routes.cart_url}.json`,
                {
                  headers: {
                    Accept:
                      'application/json',
                  },

                  credentials:
                    'same-origin',
                }
              );

            if (!cartResponse.ok) {
              throw new Error(
                `Unable to refresh cart: ${cartResponse.status}`
              );
            }

            cart =
              await cartResponse.json();
          }

          /*
           * Resolve Horizon's event.
           *
           * cart-items-component will morph
           * cart-drawer-section.
           *
           * Our handleCartUpdate() listener above
           * will then reload recommendations.
           */
          deferredEventPromise.resolve({
            cart:
              CartLinesUpdateEvent
                .createCartFromAjaxResponse(
                  cart
                ),

            detail: {
              items: cart.items,

              source:
                'cart-recommendations',

              sourceId:
                'cart-recommendations',

              itemCount: 1,

              productId,

              sections:
                data.sections,

              didError: false,
            },
          });
        } catch (error) {
          console.error(
            'Unable to add recommended product:',
            error
          );

          deferredEventPromise.reject(
            error
          );

          if (button.isConnected) {
            button.disabled = false;

            button.removeAttribute(
              'aria-busy'
            );

            button.textContent =
              originalText;
          }
        }
      }
    }
  );
}